/**
 * All Device Lectures Page - Frontend
 * @description Página que muestra todas las lecturas de un dispositivo específico
 * Basada en OperationLecturesPage pero sin detalles de operación específica
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, FileSpreadsheet, RefreshCw } from 'lucide-react';

// Components
import { LecturesTable } from '@/components/features/operations';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';

// Services and mappers
import { deviceService, dashboardService } from '@/services/api';
import { formatDateAsYearMonthDayHourMinute } from '@/utils/formatters/dateFormatter';

// Hooks and contexts
import { useToast } from '@/contexts/ToastContext';
import { useNavigationHelpers } from '@/utils/navigationHelpers';
import { useExcelExport } from '@/hooks/useExcelExport';
import { useAuth } from '@/contexts/AuthContext';

// Types
import { LecturesTableData, UILectureEvent } from '@/types/operational-ui.types';
import { Device } from '@/types/device.types';

export const AllDeviceLecturesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { navigateWithTokenCheck } = useNavigationHelpers();
  const { user } = useAuth();
  const { deviceId } = useParams<{ deviceId: string }>();
  const { exportLectures, generateFileName, isExporting } = useExcelExport({ showToasts: false });

  const [device, setDevice] = useState<Device | null>(null);
  const [lectures, setLectures] = useState<LecturesTableData[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(true);

  // Security validation function
  const validateDeviceAccess = useCallback(async () => {
    if (!user?.id || !deviceId) {
      showToast(t('errors.userNotAuthenticated'), 'error');
      await navigateWithTokenCheck(navigate, '/operations');
      return;
    }

    try {
      setIsCheckingSecurity(true);

      // For Administrators, they can access any device
      if (user.role === 'Administrador') {
        setIsCheckingSecurity(false);
        return;
      }

      // For Supervisors and Operarios, check if they have access to this specific device
      if (user.role === 'Supervisor' || user.role === 'Operario') {
        const userDevicesResponse = await deviceService.getByUserId(user.id);

        if (!userDevicesResponse.success || !userDevicesResponse.data) {
          showToast(t('errors.failedToCheckDeviceAccess'), 'error');
          await navigateWithTokenCheck(navigate, '/operations');
          return;
        }

        // Check if the device ID is in the user's associated devices
        const hasAccess = userDevicesResponse.data.some((device) => device.id === deviceId);

        if (!hasAccess) {
          showToast(t('errors.youDontHaveAccessToThisDevice'), 'error');
          await navigateWithTokenCheck(navigate, '/operations');
          return;
        }
      }

      setIsCheckingSecurity(false);
    } catch (error) {
      console.error('Error validating device access:', error);
      showToast(t('errors.securityValidationFailed'), 'error');
      await navigateWithTokenCheck(navigate, '/operations');
    }
  }, [user, deviceId, t, showToast, navigate, navigateWithTokenCheck]);

  const mapDashboardLectureToTableData = useCallback(
    (dashboardLecture: any): LecturesTableData => ({
      id: dashboardLecture.id,
      lectureIdentifier: dashboardLecture.lectureIdentifier,
      deviceId: dashboardLecture.deviceId,
      operationId: dashboardLecture.operationId,
      timestamp: dashboardLecture.timestamp,
      tz: dashboardLecture.tz,
      coordinates: dashboardLecture.coordinates,
      eventType: dashboardLecture.eventType as UILectureEvent,
      inclinationAngle: dashboardLecture.inclinationAngle,
      angleLimits: dashboardLecture.angleLimits,
      sequence: dashboardLecture.sequence,
      timestampFormatted: formatDateAsYearMonthDayHourMinute(dashboardLecture.timestamp, dashboardLecture.tz),
      coordinatesFormatted: `${dashboardLecture.coordinates.latitude.toFixed(6)}, ${dashboardLecture.coordinates.longitude.toFixed(6)}`,
      inclinationAngle_lateral: dashboardLecture.inclinationAngle.lateral,
      inclinationAngle_longitudinal: dashboardLecture.inclinationAngle.longitudinal,
      angleLimits_lateral: dashboardLecture.angleLimits.lateral,
      angleLimits_longitudinal: dashboardLecture.angleLimits.longitudinal,
      lectureId: dashboardLecture.id,
    }),
    []
  );

  const loadData = useCallback(async () => {
    if (!deviceId) return;

    try {
      setLoading(true);

      const [deviceResponse, lecturesPageResponse] = await Promise.all([
        deviceService.getById(deviceId),
        dashboardService.getLecturesByDeviceIdPage(deviceId, { limit: 300 }),
      ]);

      // Handle device response
      if (!deviceResponse.success) {
        throw new Error(deviceResponse.error.message || 'Device not found');
      }
      if (!deviceResponse.data) {
        throw new Error('Device not found');
      }
      setDevice(deviceResponse.data);

      // Handle lectures response
      if (!lecturesPageResponse.success) {
        console.warn('No lectures found for device:', deviceId);
        setLectures([]);
        setHasMore(false);
        setNextCursor(null);
        return;
      }

      const tableData = lecturesPageResponse.data.items.map(mapDashboardLectureToTableData);
      setLectures(tableData);
      setHasMore(lecturesPageResponse.data.hasMore);
      setNextCursor(lecturesPageResponse.data.nextCursor);
    } catch (error) {
      console.error('Error loading device lectures:', error);
      showToast(t('pages.lectures.loadError'), 'error');

      // If not found, redirect back
      if (error instanceof Error && error.message.includes('not found')) {
        await navigateWithTokenCheck(navigate, `/operations`);
      }
    } finally {
      setLoading(false);
    }
  }, [deviceId, navigate, showToast, t, mapDashboardLectureToTableData]);

  const handleLoadMore = useCallback(async () => {
    if (!deviceId || !nextCursor || isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      const response = await dashboardService.getLecturesByDeviceIdPage(deviceId, {
        limit: 300,
        cursor: nextCursor,
      });
      if (!response.success) return;
      const nextRows = response.data.items.map(mapDashboardLectureToTableData);
      setLectures((prev) => [...prev, ...nextRows]);
      setHasMore(response.data.hasMore);
      setNextCursor(response.data.nextCursor);
    } finally {
      setIsLoadingMore(false);
    }
  }, [deviceId, nextCursor, isLoadingMore, hasMore, mapDashboardLectureToTableData]);

  // Security validation effect
  useEffect(() => {
    if (user?.id && deviceId) {
      validateDeviceAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, deviceId]); // Remove validateDeviceAccess from dependencies to prevent loop

  useEffect(() => {
    // Only load data if security check passed
    if (!isCheckingSecurity) {
      loadData();
    }
  }, [isCheckingSecurity, loadData]);

  const handleRefresh = () => {
    loadData();
  };

  const deviceName = device?.vehicle?.plate || device?.serialNumber || deviceId;

  const downloadExportBlob = useCallback((blob: Blob, fileName: string) => {
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(url);
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!deviceId) return;
    dashboardService
      .exportLecturesByDevice(deviceId, 'csv')
      .then((blob) => {
        downloadExportBlob(
          blob,
          `Todas_las_lecturas_del_dispositivo_${deviceName}_${new Date().toISOString().split('T')[0]}.csv`
        );
        showToast(t('common.exportSuccessful'), 'success');
      })
      .catch(() => showToast(t('common.exportError'), 'error'));
  }, [deviceId, deviceName, downloadExportBlob, showToast, t]);

  // Función para exportar a XLSX
  const handleExportXLSX = useCallback(async () => {
    try {
      if (!deviceId) return;
      const blob = await dashboardService.exportLecturesByDevice(deviceId, 'xlsx');
      const fileName = generateFileName('lecturas', deviceName);
      downloadExportBlob(blob, `${fileName}.xlsx`);
      showToast(t('common.exportSuccessful'), 'success');
    } catch (error) {
      showToast(
        `❌ Error al exportar: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        'error'
      );
    }
  }, [deviceId, deviceName, generateFileName, showToast, t, downloadExportBlob]);

  // Show loading while checking security
  if (isCheckingSecurity) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Loading Overlay - Reusable Component */}
      <LoadingOverlay
        loading={loading}
        title={t('common.loading')}
        message={t('pages.lectures.loadingLectures')}
      />

      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center space-x-4">
          {/*
          <button
            onClick={handleBackToDevices}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </button>
          */}

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('pages.lectures.allDeviceLectures')}
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">
                  {t('pages.lectures.deviceSerialNumber')}
                </span>
                <span className="ml-2 text-gray-900">{deviceName}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  {t('pages.lectures.totalLectures')}:
                </span>
                <span className="ml-2 text-gray-900">{lectures.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}

        <div className="flex gap-3">
          {handleRefresh && (
            <button
              onClick={handleRefresh}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.refresh')}
            </button>
          )}
          <button
            onClick={handleExportCSV}
            disabled={lectures.length === 0}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </button>

          <button
            onClick={handleExportXLSX}
            disabled={lectures.length === 0 || isExporting}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Exportando...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Exportar XLSX
              </>
            )}
          </button>
        </div>
      </div>

      {/* Lectures Table */}
      <LecturesTable
        lectures={lectures}
        loading={false}
        showSequence={true} // Show sequence column for device-level lectures
        showOperationId={true} // Show operation id column for device-level lectures
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />
    </div>
  );
};
